import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchEventDetail,
  fetchEventByToken,
  registerForSession,
  batchRegisterForSession,
  type CatalogEvent,
  type CatalogSession,
  type RegistrationRequest,
  type OrgBrandingInfo,
} from '../../api/learnPublicApi';
import {
  MapPin, User, CalendarBlank, Monitor, Buildings, Broadcast,
  Envelope, WarningCircle, GraduationCap, ArrowRight, ArrowLeft, Clock,
  CheckCircle, X, Users as UsersIcon, ListChecks, CaretRight, Plus, Trash,
} from '@phosphor-icons/react';
import { useCatalogPath } from './TrainingTopbar';

/* ── helpers ── */

function isZeroDate(d?: string) {
  if (!d) return true;
  return d.startsWith('0000') || d.startsWith('0001') || d === '';
}

function seatCfg(available: number, max: number) {
  if (max <= 0) return { bar: 'bg-gray-300', text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
  const pct = available / max;
  if (pct > 0.4) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  if (pct > 0.15) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { bar: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' };
}

function formatDate(d?: string) {
  if (!d || isZeroDate(d)) return '';
  const normalised = d.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d + 'T00:00:00' : d;
  const date = new Date(normalised);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateShort(d?: string) {
  if (!d || isZeroDate(d)) return '';
  // Normalise YYYY-MM-DD to avoid timezone-offset "Invalid Date" on some engines
  const normalised = d.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d + 'T00:00:00' : d;
  const date = new Date(normalised);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(t?: string) {
  if (!t || isZeroDate(t)) return '';
  if (t.includes('T')) {
    const d = new Date(t);
    if (isNaN(d.getTime()) || d.getFullYear() <= 1) return '';
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  // HH:MM:SS or HH:MM — return only HH:MM
  return t.substring(0, 5);
}

function getModeLabel(mode: string) {
  if (!mode) return 'Virtual';
  const m = mode.toLowerCase();
  if (m === 'onsite' || m === 'in_person' || m === 'in-person' || m === 'vilt') return 'Onsite';
  if (m === 'hybrid') return 'Hybrid';
  return 'Virtual';
}

function ModeIcon({ mode, size = 14 }: { mode: string; size?: number }) {
  const m = getModeLabel(mode);
  if (m === 'Onsite') return <Buildings size={size} weight="bold" />;
  if (m === 'Hybrid') return <Broadcast size={size} weight="bold" />;
  return <Monitor size={size} weight="bold" />;
}

function FormInput({ label, required, optional, ...props }: {
  label: string; required?: boolean; optional?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <label className="block text-[13px] font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-rose-500 ml-0.5">*</span>}
        {optional && <span className="text-[11px] font-normal text-gray-400 ml-1">(optional)</span>}
      </label>
      <input
        {...props}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none transition-all
          focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 placeholder:text-gray-400"
      />
    </div>
  );
}

/* ── main component ── */

export default function TrainingEventDetailPage() {
  const { id, token } = useParams<{ id?: string; token?: string }>();
  const navigate = useNavigate();
  const catalogPath = useCatalogPath();
  const [event, setEvent] = useState<CatalogEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSession, setSelectedSession] = useState<CatalogSession | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ magicToken: string; message: string } | null>(null);
  const [regError, setRegError] = useState<string | null>(null);
  const [orgBranding, setOrgBranding] = useState<OrgBrandingInfo | null>(null);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [orgName, setOrgName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);

  // Multi-person registration
  interface Registrant { email: string; first_name: string; last_name: string; phone: string; organization_name: string; }
  const [additionalRegistrants, setAdditionalRegistrants] = useState<Registrant[]>([]);
  const addRegistrant = () => setAdditionalRegistrants(prev => [...prev, { email: '', first_name: '', last_name: '', phone: '', organization_name: '' }]);
  const removeRegistrant = (idx: number) => setAdditionalRegistrants(prev => prev.filter((_, i) => i !== idx));
  const updateRegistrant = (idx: number, field: keyof Registrant, value: string) => {
    setAdditionalRegistrants(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  useEffect(() => {
    setLoading(true);
    const fetchEvent = token
      ? fetchEventByToken(token)
      : id
        ? fetchEventDetail(Number(id))
        : Promise.reject('No event identifier');

    fetchEvent
      .then((data) => {
        setEvent(data);
        if (data?.organization) setOrgBranding(data.organization);
      })
      .catch(() => setError('Event not found or the link has expired'))
      .finally(() => setLoading(false));
  }, [id, token]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSession || !event) return;
    setSubmitting(true);
    setRegError(null);

    const eventId = event.event_id || event.id;
    const sessionId = selectedSession.id;

    // Primary registrant
    const primaryReq: RegistrationRequest = {
      email,
      first_name: firstName,
      last_name: lastName || undefined,
      phone: phone || undefined,
      organization_name: orgName || undefined,
      job_title: jobTitle || undefined,
      consent_given: consentGiven,
      share_token: token || undefined,
    };

    // Additional registrants (if any)
    const validAdditional = additionalRegistrants.filter(r => r.email.trim() && r.first_name.trim());

    try {
      if (validAdditional.length === 0) {
        // Single registration (original flow)
        const result = await registerForSession(eventId, sessionId, primaryReq);
        setSuccess({
          magicToken: result.magic_token,
          message: result.registration_status === 'approved'
            ? 'Registration successful! Check your email for details.'
            : 'You have been added to the waitlist. We will notify you when a spot opens up.',
        });
      } else {
        // Batch registration: primary + additional
        const allRegistrants: RegistrationRequest[] = [
          primaryReq,
          ...validAdditional.map(r => ({
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name || undefined,
            phone: r.phone || undefined,
            organization_name: r.organization_name || orgName || undefined,
            share_token: token || undefined,
          })),
        ];
        const results = await batchRegisterForSession(eventId, sessionId, allRegistrants);
        const successCount = results.filter(r => r.success).length;
        const failedResults = results.filter(r => !r.success);

        if (failedResults.length > 0) {
          setRegError(`${successCount} registered, ${failedResults.length} failed: ${failedResults.map(r => `${r.email} — ${r.error}`).join('; ')}`);
        }
        if (successCount > 0) {
          const primaryResult = results.find(r => r.email === email && r.success);
          setSuccess({
            magicToken: primaryResult?.result?.magic_token || '',
            message: `${successCount} of ${allRegistrants.length} participant${successCount > 1 ? 's' : ''} registered successfully!`,
          });
        }
      }
      setShowForm(false);
      setAdditionalRegistrants([]);
    } catch (err: any) {
      setRegError(err.response?.data?.message || err.response?.data?.error || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="relative w-10 h-10">
          <div className="absolute inset-0 rounded-full border-2 border-gray-200" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-600 animate-spin" />
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 max-w-sm text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <WarningCircle size={28} weight="fill" className="text-rose-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-sm text-gray-500 mb-5">{error}</p>
          <button
            onClick={() => navigate(catalogPath)}
            className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors border-none cursor-pointer"
          >
            Browse Catalog
          </button>
        </div>
      </div>
    );
  }

  /* ── Success Screen ── */
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 overflow-auto">
        {/* Back bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3">
          <div className="max-w-3xl mx-auto">
            <button onClick={() => navigate(catalogPath)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer">
              <ArrowLeft size={16} weight="bold" /> Back to Catalog
            </button>
          </div>
        </div>

        <main className="max-w-xl mx-auto px-6 py-10">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-emerald-100 rounded-full inline-flex items-center justify-center mb-4">
              <CheckCircle size={28} weight="fill" className="text-emerald-500" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">You're Registered!</h1>
            <p className="text-sm text-gray-500">{success.message}</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Registration Summary</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><span className="text-gray-400 text-xs block mb-0.5">Event</span><span className="font-medium text-gray-900">{event.event_name}</span></div>
              <div><span className="text-gray-400 text-xs block mb-0.5">Session</span><span className="font-medium text-gray-900">{selectedSession?.session_name || '—'}</span></div>
              <div><span className="text-gray-400 text-xs block mb-0.5">Date</span>{selectedSession ? formatDateShort(selectedSession.start_date) : '—'}{selectedSession?.start_time ? ` · ${formatTime(selectedSession.start_time)}` : ''}</div>
              <div><span className="text-gray-400 text-xs block mb-0.5">Email</span>{email}</div>
            </div>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Envelope size={18} weight="duotone" className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-[13px] text-emerald-700">
              <strong>Check your email</strong> for confirmation details, calendar invite, and registration management link.
            </div>
          </div>

          <button
            onClick={() => navigate(catalogPath)}
            className="w-full px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center justify-center gap-1.5 border-none cursor-pointer"
          >
            Browse More Events <ArrowRight size={14} />
          </button>
        </main>
      </div>
    );
  }

  /* ── Derived values ── */
  const sessionCount = event.sessions?.length || 0;
  const firstSession = event.sessions?.[0];
  const rawMode = event.event_mode || firstSession?.mode || 'in_person';
  const mode = getModeLabel(rawMode);
  const totalSpots = event.sessions?.reduce((a, s) => a + (s.available_spots || 0), 0) || 0;
  const totalEnrolled = event.sessions?.reduce((a, s) => a + (s.enrolled_count || 0), 0) || 0;
  const totalMax = event.sessions?.reduce((a, s) => a + (s.max_participants || 0), 0) || 0;
  const trainerName = firstSession?.trainer_name || event.external_trainer || '';
  const locationName = firstSession?.location_name || event.external_location || '';

  /* ── Main Event Detail ── */
  return (
    <div className="min-h-screen bg-gray-50 overflow-auto flex flex-col">

      {/* ═══ Top Bar ═══ */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(catalogPath)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer transition-colors">
            <ArrowLeft size={16} weight="bold" />
            <span className="font-medium">Back to Catalog</span>
          </button>
          <div className="flex items-center gap-2">
            {orgBranding?.logo ? (
              <img src={orgBranding.logo} alt="" className="w-6 h-6 object-contain rounded" />
            ) : orgBranding?.company_name ? (
              <div className="w-6 h-6 rounded bg-indigo-600 flex items-center justify-center">
                <span className="text-white text-[10px] font-bold">{orgBranding.company_name.charAt(0)}</span>
              </div>
            ) : null}
            {orgBranding?.company_name && (
              <span className="text-sm font-medium text-gray-600">{orgBranding.company_name}</span>
            )}
          </div>
        </div>
      </div>

      {/* ═══ Hero Banner (dark, inspired by Learning Path) ═══ */}
      <div className="bg-slate-900 text-white flex-shrink-0 relative overflow-hidden">
        {/* Subtle glow */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-indigo-600/10 rounded-full blur-3xl" />

        <div className="max-w-4xl mx-auto px-6 py-8 relative z-10">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide rounded-md bg-white/10 text-white/90 backdrop-blur-sm">
              <ModeIcon mode={rawMode} size={12} /> {mode}
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide rounded-md backdrop-blur-sm ${
              event.registration_restriction === 'public'
                ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-amber-500/20 text-amber-300'
            }`}>
              {event.registration_restriction === 'public' ? 'Open Enrollment' : event.registration_restriction === 'restricted' ? 'Restricted' : 'By Invitation'}
            </span>
            {event.auto_approve_external && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide rounded-md bg-blue-500/20 text-blue-300 backdrop-blur-sm">
                <CheckCircle size={12} weight="bold" /> Instant Confirmation
              </span>
            )}
          </div>

          {/* Title + description */}
          <h1 className="text-2xl font-bold mb-1.5 tracking-tight">{event.event_name}</h1>
          {event.course_description && (
            <p className="text-sm text-slate-400 mb-4 max-w-2xl leading-relaxed line-clamp-2">{event.course_description}</p>
          )}

          {/* Stats row */}
          <div className="flex flex-wrap gap-6 text-sm text-slate-300">
            {trainerName && (
              <span className="inline-flex items-center gap-1.5"><User size={14} className="text-slate-400" /> {trainerName}</span>
            )}
            {locationName && (
              <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-slate-400" /> {locationName}</span>
            )}
            {firstSession?.start_date && !isZeroDate(firstSession.start_date) && (
              <span className="inline-flex items-center gap-1.5"><CalendarBlank size={14} className="text-slate-400" /> {formatDateShort(firstSession.start_date)}</span>
            )}
            <span className="inline-flex items-center gap-1.5"><ListChecks size={14} className="text-slate-400" /> {sessionCount} session{sessionCount !== 1 ? 's' : ''}</span>
            <span className="inline-flex items-center gap-1.5"><UsersIcon size={14} className="text-slate-400" /> {totalSpots > 0 ? `${totalSpots} spots open` : `${totalEnrolled} enrolled`}</span>
          </div>

          {/* Quick stats cards */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10">
              <div className="text-xl font-bold text-white">{sessionCount}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">Sessions</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10">
              <div className="text-xl font-bold text-emerald-400">{totalSpots}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">Spots Open</div>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10">
              <div className="text-xl font-bold text-indigo-400">{totalEnrolled}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wider">Enrolled</div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ Sessions List ═══ */}
      <main className="max-w-4xl mx-auto px-6 py-6 w-full flex-1">
        <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          Available Sessions
          <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sessionCount}</span>
        </h2>

        {(!event.sessions || event.sessions.length === 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
            No sessions available for this event.
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {event.sessions?.map((session, idx) => {
            const pct = session.max_participants > 0
              ? (session.max_participants - session.available_spots) / session.max_participants
              : 0;
            const cfg = seatCfg(session.available_spots, session.max_participants);
            const isFull = session.available_spots <= 0;
            const time = formatTime(session.start_time);

            return (
              <div
                key={session.id}
                className="bg-white border border-gray-200 rounded-lg px-5 py-4 hover:border-indigo-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between gap-4">
                  {/* Left: Session info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{session.session_name}</h3>
                      {!isFull ? (
                        <span className={`px-2 py-0.5 text-[10px] font-semibold ${cfg.text} ${cfg.bg} border ${cfg.border} rounded-full whitespace-nowrap flex-shrink-0`}>
                          {session.available_spots} spot{session.available_spots !== 1 ? 's' : ''} left
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full whitespace-nowrap flex-shrink-0">
                          {event.allow_waitlist ? 'Waitlist' : 'Full'}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      {session.start_date && !isZeroDate(session.start_date) && (
                        <span className="inline-flex items-center gap-1"><CalendarBlank size={12} className="text-gray-400" /> {formatDate(session.start_date)}</span>
                      )}
                      {time && (
                        <span className="inline-flex items-center gap-1"><Clock size={12} className="text-gray-400" /> {time}</span>
                      )}
                      {session.location_name && (
                        <span className="inline-flex items-center gap-1"><MapPin size={12} className="text-gray-400" /> {session.location_name}</span>
                      )}
                      {session.trainer_name && (
                        <span className="inline-flex items-center gap-1"><User size={12} className="text-gray-400" /> {session.trainer_name}</span>
                      )}
                    </div>
                    {/* Seat bar */}
                    {session.max_participants > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-20 h-1 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${cfg.bar} transition-all duration-500`} style={{ width: `${Math.min(pct * 100, 100)}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-400">{session.max_participants - session.available_spots}/{session.max_participants}</span>
                      </div>
                    )}
                  </div>

                  {/* Right: CTA */}
                  {(() => {
                    const sessionStarted = session.start_date && !isZeroDate(session.start_date) && new Date(session.start_date) <= new Date();
                    if (sessionStarted) {
                      return <span className="text-xs font-medium text-gray-400 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">Registration closed</span>;
                    }
                    if (!isFull) {
                      return (
                        <button
                          onClick={() => { setSelectedSession(session); setShowForm(true); setRegError(null); }}
                          className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors whitespace-nowrap border-none cursor-pointer flex items-center gap-1"
                        >
                          Register <CaretRight size={12} weight="bold" />
                        </button>
                      );
                    }
                    if (event.allow_waitlist) {
                      return (
                        <button
                          onClick={() => { setSelectedSession(session); setShowForm(true); setRegError(null); }}
                          className="px-4 py-2 text-xs font-semibold text-indigo-600 bg-transparent border border-indigo-300 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Join Waitlist
                        </button>
                      );
                    }
                    return <span className="text-xs font-medium text-gray-400">Full</span>;
                  })()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Event details (compact, inline — not a separate sidebar) */}
        {(event.course_type || trainerName || locationName) && (
          <div className="mt-6 bg-white border border-gray-200 rounded-lg px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Event Details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              {event.course_type && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Type</div>
                  <div className="text-gray-800 capitalize">{event.course_type}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Format</div>
                <div className="text-gray-800 inline-flex items-center gap-1"><ModeIcon mode={rawMode} size={13} /> {mode}</div>
              </div>
              {trainerName && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Instructor</div>
                  <div className="text-gray-800">{trainerName}</div>
                </div>
              )}
              {locationName && (
                <div>
                  <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Location</div>
                  <div className="text-gray-800">{locationName}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* ═══ Footer ═══ */}
      <footer className="border-t border-gray-200 mt-auto flex-shrink-0">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="text-[11px] text-gray-400">&copy; {new Date().getFullYear()} {orgBranding?.company_name || 'Trone'}</span>
          <span className="text-[11px] text-gray-300">Powered by Trainery</span>
        </div>
      </footer>

      {/* ═══ Registration Form Modal ═══ */}
      {showForm && selectedSession && (
        <div
          className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="bg-white rounded-xl max-w-[560px] w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            {/* Modal header */}
            <div className="bg-slate-50 border-b border-gray-200 rounded-t-xl px-5 py-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={16} weight="duotone" className="text-indigo-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">{event.event_name}</div>
                <div className="text-xs text-gray-500">{selectedSession.session_name}{selectedSession.start_date && !isZeroDate(selectedSession.start_date) ? ` · ${formatDateShort(selectedSession.start_date)}` : ''}</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0 transition-colors bg-transparent border-none cursor-pointer">
                <X size={18} />
              </button>
            </div>

            {/* Form */}
            <div className="p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Register for this Session</h3>

              {regError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-4 flex items-start gap-2">
                  <WarningCircle size={14} weight="fill" className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-700">{regError}</div>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormInput label="First Name" required type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="John" />
                  <FormInput label="Last Name" required type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Doe" />
                </div>
                <FormInput label="Email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="john.doe@company.com" />
                <div className="grid grid-cols-2 gap-3">
                  <FormInput label="Organization" type="text" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Company name" />
                  <FormInput label="Phone" optional type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000-0000" />
                </div>

                {/* Additional Registrants */}
                {additionalRegistrants.length > 0 && (
                  <div className="border-t pt-3 mt-1 space-y-2.5">
                    <p className="text-[13px] font-medium text-gray-700">Additional Participants</p>
                    {additionalRegistrants.map((reg, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-gray-50 border border-gray-100 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold flex items-center justify-center">
                              {idx + 2}
                            </span>
                            <span className="text-xs font-medium text-gray-500">Participant {idx + 2}</span>
                          </span>
                          <button type="button" onClick={() => removeRegistrant(idx)}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" placeholder="First name *" value={reg.first_name}
                            onChange={(e) => updateRegistrant(idx, 'first_name', e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 outline-none placeholder:text-gray-400"
                          />
                          <input type="text" placeholder="Last name" value={reg.last_name}
                            onChange={(e) => updateRegistrant(idx, 'last_name', e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 outline-none placeholder:text-gray-400"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="email" placeholder="Email *" value={reg.email}
                            onChange={(e) => updateRegistrant(idx, 'email', e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 outline-none placeholder:text-gray-400"
                          />
                          <input type="tel" placeholder="Phone (optional)" value={reg.phone}
                            onChange={(e) => updateRegistrant(idx, 'phone', e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 outline-none placeholder:text-gray-400"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add person button */}
                <button type="button" onClick={addRegistrant}
                  className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium pt-1 cursor-pointer"
                >
                  <Plus size={14} weight="bold" />
                  Add another person
                </button>

                {/* Consent */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox" id="consent" required checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 accent-indigo-600 flex-shrink-0 cursor-pointer"
                  />
                  <label htmlFor="consent" className="text-[11px] text-gray-500 leading-relaxed cursor-pointer">
                    I consent to the processing of my personal data for event registration and communication.
                  </label>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setShowForm(false); setAdditionalRegistrants([]); }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                  >Cancel</button>
                  <button type="submit" disabled={submitting}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-1 border-none cursor-pointer"
                  >
                    {submitting ? 'Registering...' : (
                      <>Register {additionalRegistrants.filter(r => r.email && r.first_name).length > 0 ? `${1 + additionalRegistrants.filter(r => r.email && r.first_name).length} people` : ''} <ArrowRight size={14} /></>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
